import React, { useState, useMemo } from "react";
import {
  TextField,
  Grid,
  MenuItem,
  Box,
  Paper,
  IconButton,
  Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import FolderIcon from "@mui/icons-material/Folder";
import LockResetIcon from "@mui/icons-material/LockReset";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from 'react-i18next';

const OrganizationUserGrid = ({
  users,
  onEditProjects, // Nueva función para editar proyectos
  onEditPassword,
  onEditUser,
  onDeleteUser,
  currentUserRole, // Rol del usuario actual
  currentUserId, // ID del usuario actual
}) => {
  const [filters, setFilters] = useState({
    name: "",
    email: "",
    role: "",
    project: "",
  });
  const [pageSize, setPageSize] = useState(10);
  
  const { t } = useTranslation();

  const uniqueProjects = useMemo(() => {
    return Array.from(new Set(users.flatMap((u) => u.projectNames || [])));
  }, [users]);

  const uniqueRoles = useMemo(() => {
    return Array.from(new Set(users.map((u) => u.rol).filter(Boolean)));
  }, [users]);

  const handleChange = (e) => {
    setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // Función para determinar si el usuario actual puede editar a otro usuario
  const canEditUser = (targetUser) => {
    // Si es el mismo usuario, siempre puede editarse a sí mismo (datos básicos)
    if (targetUser.uid === currentUserId) {
      return true;
    }

    // Los administradores de organización pueden editar usuarios normales
    if (["super-admin", "admin-grafton", "admin"].includes(currentUserRole) && targetUser.rol === "user") {
      return true;
    }

    return false;
  };

  // Función para determinar si puede editar proyectos
  const canEditProjects = () => {
    return ["super-admin", "admin-grafton", "admin"].includes(currentUserRole);
  };

  const filteredUsers = users.filter((u) => {
    const nameMatch = u.name?.toLowerCase().includes(filters.name.toLowerCase());
    const emailMatch = u.email?.toLowerCase().includes(filters.email.toLowerCase());
    const roleMatch = filters.role === "" || u.rol === filters.role;
    const projMatch =
      filters.project === "" || u.projectNames?.includes(filters.project);
    return nameMatch && emailMatch && roleMatch && projMatch;
  });

  const columns = [
    { field: "name", headerName: t('name'), flex: 1.2 },
    { field: "email", headerName: t('email'), flex: 1.5 },
    { field: "rol", headerName: t('role'), flex: 0.8 },
    {
      field: "projectNames",
      headerName: t('projects.title'),
      flex: 1.5,
      sortable: false,
      renderCell: (params) => {
        const projects = params.value || [];
        return (
          <Box sx={{ py: 1 }}>
            {projects.length > 0 ? (
              projects.map((project, index) => (
                <Box
                  key={index}
                  component="span"
                  sx={{
                    display: "inline-block",
                    bgcolor: "primary.light",
                    color: "primary.contrastText",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    mr: 0.5,
                    mb: 0.5,
                  }}
                >
                  {project}
                </Box>
              ))
            ) : (
              <Box
                component="span"
                sx={{
                  color: "text.secondary",
                  fontStyle: "italic",
                  fontSize: "0.875rem",
                }}
              >
                {t('users.noProjects')}
              </Box>
            )}
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: t('actions'),
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const canEdit = canEditUser(params.row);
        const canEditProj = canEditProjects(params.row);

        return (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            {/* Editar proyectos */}
            <Tooltip title={canEditProj ? t('users.changeProjects') : t('users.noEditAllowed')}>
              <span>
                <IconButton
                  size="small"
                  disabled={!canEditProj}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditProjects?.(params.row);
                  }}
                >
                  <FolderIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            {/* Cambiar contraseña */}
            <Tooltip title={canEdit ? t('users.changePassword') : t('users.noEditAllowed')}>
              <span>
                <IconButton
                  size="small"
                  disabled={!canEdit}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditPassword?.(params.row);
                  }}
                >
                  <LockResetIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            {/* Editar datos generales */}
            <Tooltip title={canEdit ? t('users.generalEdit') : t('users.noEditAllowed')}>
              <span>
                <IconButton
                  size="small"
                  disabled={!canEdit}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditUser?.(params.row);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            {/* Eliminar usuario */}
            <Tooltip title={canEdit ? t('users.deleteUser') : t('users.noEditAllowed')}>
              <span>
                <IconButton
                  size="small"
                  disabled={!canEdit}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteUser?.(params.row);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  return (
    <>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label={t('name')}
              name="name"
              fullWidth
              value={filters.name}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label={t('email')}
              name="email"
              fullWidth
              value={filters.email}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              label={t('role')}
              name="role"
              fullWidth
              value={filters.role}
              onChange={handleChange}
            >
              <MenuItem value="">{t('all')}</MenuItem>
              {uniqueRoles.map((role, idx) => (
                <MenuItem key={idx} value={role}>
                  {t(role)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              label={t('project')}
              name="project"
              fullWidth
              value={filters.project}
              onChange={handleChange}
            >
              <MenuItem value="">{t('all')}</MenuItem>
              {uniqueProjects.map((proj, idx) => (
                <MenuItem key={idx} value={proj}>
                  {proj}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ height: 500, width: "100%" }}>
        <DataGrid
          rows={filteredUsers}
          columns={columns}
          pageSize={pageSize}
          onPageSizeChange={(newSize) => setPageSize(newSize)}
          rowsPerPageOptions={[10, 20, 50]}
          disableSelectionOnClick
        />
      </Box>
    </>
  );
};

export default OrganizationUserGrid;